import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import { HEARING_TYPE, NOTIFICATIONTYPE_TYPE } from '../../constants/api';
import { API_REDUCER } from '../../constants/store';
import {
  getNotificationContentSpecificationForHearingError,
  getNotificationContentSpecificationForHearingInitialize,
  getNotificationContentSpecificationForHearingSuccess,
} from '../actions/api';
import { NotificationContentSpecificationConnector } from '../connectors/notificationContentSpecificationConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { updateDtoList } from '../helpers/reducerHelpers';
import { generateGetAllApiCall } from '../helpers/sagaHelpers';
import {
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { notificationTypeByTypeSelector } from './notificationTypeSlice';

// -------- Overview --------
// The following endpoints exist
//  - GET /hearing/{hearingId}/notificationContentSpecification

// -------- Reducer --------
const notificationContentSpecificationReducer = createReducer(
  {
    all: [],
    isFetching: false,
  },
  (builder) => {
    builder
      .addCase(
        getNotificationContentSpecificationForHearingInitialize,
        (state) => {
          state.isFetching = true;
        },
      )
      .addCase(getNotificationContentSpecificationForHearingError, (state) => {
        state.isFetching = false;
      })
      .addCase(
        getNotificationContentSpecificationForHearingSuccess,
        (state, action) => {
          const newState = Object.assign({}, state, {
            isFetching: false,
          });

          const notificationContentSpecification = getRequestData(action);
          return updateDtoList(
            newState,
            'all',
            notificationContentSpecification,
          );
        },
      );
  },
);

// --------- Sagas ----------
export function* notificationContentSpecificationSaga() {
  yield all([fork(getNotificationContentSpecificationForHearing)]);
}

function* getNotificationContentSpecificationForHearing() {
  yield takeEvery(
    getNotificationContentSpecificationForHearingInitialize.type,
    generateGetAllApiCall(NotificationContentSpecificationConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.getNotificationContentSpecification],
        action.payload.hearingId,
      );
    }),
  );
}

// -------- Selectors --------
const selectAllNotificationContentSpecifications = (state) =>
  selectAllFromApiState(state, API_REDUCER.NOTIFICATION_CONTENT_SPECIFICATION);

const allNotificationContentSpecificationsSelector = createSelector(
  selectAllNotificationContentSpecifications,
  (notificationContentSpecifications) => notificationContentSpecifications,
);

const notificationContentSpecificationState = (state) =>
  state.api[API_REDUCER.NOTIFICATION_CONTENT_SPECIFICATION];

const isFetchingNotificationContentSpecificationsSelector = createSelector(
  notificationContentSpecificationState,
  (state) => state.isFetching,
);

const notificiationContentSpecificationByIdSelector = (id) =>
  createSelector(
    allNotificationContentSpecificationsSelector,
    (notificationContentSpecifications) =>
      notificationContentSpecifications.find((ncs) => getEntityId(ncs) === id),
  );

const notificationContentSpecificationsByHearingIdSelector = (hearingId) =>
  createSelector(
    allNotificationContentSpecificationsSelector,
    (notificationContentSpecifications) =>
      notificationContentSpecifications.filter(
        (ncs) => getRelId(ncs, HEARING_TYPE) === hearingId,
      ),
  );

const notificationContentSpecificationByHearingIdAndTypeSelector = (
  hearingId,
  type,
) =>
  createSelector(
    [
      notificationContentSpecificationsByHearingIdSelector(hearingId),
      notificationTypeByTypeSelector(type),
    ],
    (notificationContentSpecifications, notificationType) => {
      return notificationContentSpecifications.find(
        (ncs) =>
          getRelId(ncs, NOTIFICATIONTYPE_TYPE) ===
          getEntityId(notificationType),
      );
    },
  );

export {
  notificationContentSpecificationByHearingIdAndTypeSelector,
  notificiationContentSpecificationByIdSelector,
  isFetchingNotificationContentSpecificationsSelector,
};
export default notificationContentSpecificationReducer;
